/**
 * Turning an invitation record into the one line a row can show.
 *
 * "Invited" on its own is not actionable. An admin looking at a stalled
 * invitation needs three facts before they know what to do: how long ago it
 * went, whether it is still good, and whether the email actually left the
 * building. A resend fixes the first two; only the third tells them the problem
 * is their mail configuration and no amount of resending will help.
 */

import type { InvitationSummary } from '../api';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "just now", "3 hours ago", "2 days ago" — deliberately coarse. */
export function relativeTime(iso: string | null): string {
  if (!iso) return 'unknown';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'unknown';

  const elapsed = Date.now() - then;
  if (elapsed < MINUTE) return 'just now';
  if (elapsed < HOUR) {
    const mins = Math.round(elapsed / MINUTE);
    return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  }
  if (elapsed < DAY) {
    const hours = Math.round(elapsed / HOUR);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  const days = Math.round(elapsed / DAY);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function shortDate(iso: string | null): string {
  if (!iso) return 'unknown';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "Invited · sent 3 hours ago · expires 4 Aug 2026" */
export function inviteLine(summary: InvitationSummary | undefined): string {
  if (!summary) return 'Invited';
  const expiry = summary.expired
    ? `expired ${shortDate(summary.expiresAt)}`
    : `expires ${shortDate(summary.expiresAt)}`;
  return `Invited · sent ${relativeTime(summary.sentAt)} · ${expiry}`;
}

export interface DeliveryNote {
  tone: 'ok' | 'warn' | 'danger';
  label: string;
  /** The provider's own words, when there are any. */
  detail: string | null;
}

/**
 * Whether the last invite email landed, in words an admin can act on.
 *
 * "Skipped" is its own case rather than folded into failure: nothing broke, the
 * workspace simply has no email credentials, and the fix is a different screen.
 */
export function deliveryNote(summary: InvitationSummary | undefined): DeliveryNote {
  const delivery = summary?.lastDelivery;
  if (!delivery) return { tone: 'warn', label: 'delivery not recorded', detail: null };

  if (delivery.status === 'sent') {
    return { tone: 'ok', label: `email sent ${relativeTime(delivery.at)}`, detail: null };
  }
  if (delivery.status === 'skipped') {
    return {
      tone: 'warn',
      label: 'email not sent — no mail credentials',
      detail: delivery.skipReason,
    };
  }
  return { tone: 'danger', label: 'email failed to send', detail: delivery.errorMessage };
}
